import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getAdminDashboard,
  updateVipRegistrationMode,
} from '../../services/api';
import { useAuth } from '../auth/AuthContext';
import { motion } from 'framer-motion';
import {
  ArrowUpRight,
  Briefcase,
  Clock,
  Crown,
  RefreshCw,
  Store,
  Users,
} from 'lucide-react';
import './AdminStyles.scss';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.23, 1, 0.32, 1] } },
};

const statusClass = (status) => ({
  healthy: 'dashboard-view__pill--green',
  warning: 'dashboard-view__pill--orange',
  slowdown: 'dashboard-view__pill--orange',
  critical: 'dashboard-view__pill--red',
  cooldown: 'dashboard-view__pill--red',
  unknown: 'dashboard-view__pill--blue',
}[status] || 'dashboard-view__pill--blue');

const apiErrorMessage = (error, fallback) => error?.response?.data?.message || fallback;

const MetricCard = ({ icon: Icon, value, label, trend, iconClass }) => (
  <motion.div className="dashboard-view__metric-card" variants={itemVariants}>
    <div className="dashboard-view__metric-label">{label}</div>
    <div className="dashboard-view__metric-row">
      <span className="dashboard-view__metric-value">{value}</span>
      {trend !== null && trend !== undefined && <span className="dashboard-view__metric-badge">{trend}</span>}
    </div>
    <div className="dashboard-view__metric-content">
      <div className={`dashboard-view__metric-icon-wrap ${iconClass}`}><Icon className="dashboard-view__metric-icon" /></div>
    </div>
  </motion.div>
);

const GrowthChart = ({ series, locale, t }) => {
  const values = series || [];
  const maxValue = Math.max(1, ...values.flatMap(item => [item.users, item.creators]));
  const points = (key) => values.map((item, index) => {
    const x = values.length <= 1 ? 400 : 42 + (index * 716) / (values.length - 1);
    const y = 190 - (item[key] / maxValue) * 145;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="dashboard-view__chart dashboard-view__chart--real" role="img" aria-label={t('growth.accessibleLabel')}>
      <svg viewBox="0 0 800 240" preserveAspectRatio="none">
        {[45, 80, 115, 150, 190].map(y => <line key={y} x1="30" y1={y} x2="770" y2={y} className="dashboard-view__chart-grid" />)}
        {values.length > 0 && (
          <>
            <polyline points={points('users')} className="dashboard-view__chart-line dashboard-view__chart-line--users" />
            <polyline points={points('creators')} className="dashboard-view__chart-line dashboard-view__chart-line--creators" />
          </>
        )}
      </svg>
      <div className="dashboard-view__chart-labels">
        {values.map(item => (
          <span key={item.month}>{new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(`${item.month}T12:00:00`))}</span>
        ))}
      </div>
      <div className="dashboard-view__chart-legend">
        <span><i className="is-users" />{t('growth.users')}</span>
        <span><i className="is-creators" />{t('growth.creators')}</span>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { t, i18n } = useTranslation('admin');
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const locale = i18n.resolvedLanguage || 'es';
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardError, setDashboardError] = useState('');
  const [vipModeLoading, setVipModeLoading] = useState(false);

  const fetchDashboardData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setDashboardError('');
      const response = await getAdminDashboard();
      setDashboardData(response.data);
    } catch (error) {
      setDashboardError(apiErrorMessage(error, t('errors.dashboard')));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!currentUser?.is_staff && !currentUser?.is_superuser) return undefined;
    fetchDashboardData();
    const polling = window.setInterval(() => { if (!document.hidden) fetchDashboardData({ silent: true }); }, 30000);
    return () => window.clearInterval(polling);
  }, [currentUser, fetchDashboardData]);

  const handleToggleVipMode = async () => {
    try {
      setVipModeLoading(true);
      const response = await updateVipRegistrationMode(!dashboardData.registration_modes?.vip_mode_active);
      setDashboardData(previous => ({
        ...previous,
        vip_users: response.data.vip_users,
        registration_modes: response.data,
      }));
    } catch (error) {
      setDashboardError(apiErrorMessage(error, t('errors.vipUpdate')));
    } finally {
      setVipModeLoading(false);
    }
  };

  if (authLoading || loading) return <div className="dashboard-loading"><div className="spinner" /></div>;
  if (!currentUser || (!currentUser.is_staff && !currentUser.is_superuser)) return <Navigate to="/dashboard" />;
  if (!dashboardData) return <div className="dashboard-view__fatal-error">{dashboardError || t('errors.dashboard')}</div>;

  const health = dashboardData.meta_api_health || {};
  const refreshOps = dashboardData.instagram_refresh_ops || {};
  const runtime = refreshOps.runtime || {};
  const growthTrend = dashboardData.growth?.user_monthly_trend_percent;
  const trendLabel = growthTrend === null || growthTrend === undefined
    ? null
    : `${growthTrend > 0 ? '+' : ''}${growthTrend}%`;

  return (
    <motion.div className="dashboard-view__inner" variants={containerVariants} initial="hidden" animate="visible">
      {dashboardError && <div className="dashboard-view__alert dashboard-view__alert--error" role="alert">{dashboardError}</div>}

      <div className="dashboard-view__grid">
        <div className="dashboard-view__col-8">
          <motion.div className="dashboard-view__hero" variants={itemVariants}>
            <div className="dashboard-view__hero-row">
              <div className="dashboard-view__hero-main">
                <div className="dashboard-view__eyebrow"><RefreshCw size={10} /> {t('overview.eyebrow')}</div>
                <h1 className="dashboard-view__title">{t('overview.title')}<br />{t('overview.titleAccent')}</h1>
                <p className="dashboard-view__subtitle">{t('overview.subtitle')}</p>
              </div>
              <div className={`dashboard-view__access-card dashboard-view__access-card--vip ${dashboardData.registration_modes?.vip_mode_active ? 'is-active' : ''}`}>
                <div className="dashboard-view__access-label"><Crown size={12} /> {t('access.vip')}</div>
                <div className="dashboard-view__access-row">
                  <div className="dashboard-view__access-mode">
                    <span className="dashboard-view__access-mode-label">{t('access.newRegistrations')}</span>
                    <div className="dashboard-view__access-mode-value">{t(dashboardData.registration_modes?.vip_mode_active ? 'access.vipActive' : 'access.noLabel')}</div>
                  </div>
                  {currentUser.is_superuser && <button className="dashboard-view__button dashboard-view__button--gold" onClick={handleToggleVipMode} disabled={vipModeLoading}>
                    {t(dashboardData.registration_modes?.vip_mode_active ? 'common.turnOff' : 'common.turnOn')}
                  </button>}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="dashboard-view__metric-grid">
          <MetricCard icon={Users} label={t('metrics.users')} value={dashboardData.total_users || 0} trend={trendLabel} iconClass="dashboard-view__metric-icon-wrap--neutral" />
          <MetricCard icon={Briefcase} label={t('metrics.creators')} value={dashboardData.total_creators || 0} iconClass="dashboard-view__metric-icon-wrap--gold" />
          <MetricCard icon={Store} label={t('metrics.businesses')} value={dashboardData.total_businesses || 0} iconClass="dashboard-view__metric-icon-wrap--neutral" />
          <MetricCard icon={Clock} label={t('metrics.pending')} value={dashboardData.pending_verifications || 0} iconClass="dashboard-view__metric-icon-wrap--orange" />
          <MetricCard icon={Crown} label={t('metrics.vip')} value={dashboardData.vip_users || 0} iconClass="dashboard-view__metric-icon-wrap--gold" />
        </div>
      </div>

      <div className="dashboard-view__grid">
        <div className="dashboard-view__col-8">
          <motion.div className="dashboard-view__panel" variants={itemVariants}>
            <div className="dashboard-view__panel-header">
              <h2 className="dashboard-view__panel-title">{t('growth.title')}</h2>
              <span className="dashboard-view__panel-meta">{t('growth.subtitle')} · Comparación MTD</span>
            </div>
            <GrowthChart series={dashboardData.growth?.series} locale={locale} t={t} />
          </motion.div>
        </div>
        <div className="dashboard-view__col-4">
          <motion.div className="dashboard-view__panel" variants={itemVariants}>
            <div className="dashboard-view__panel-header">
              <h2 className="dashboard-view__panel-title">{t('system.title')}</h2>
            </div>
            <div className="dashboard-view__status-list">
              <div className="dashboard-view__status-row">
                <span>{t('system.verifications')}</span>
                <span className="dashboard-view__pill dashboard-view__pill--orange">{dashboardData.pending_verifications}</span>
              </div>
              <div className="dashboard-view__status-row">
                <span>{t('system.metaApi')}</span>
                <span className={`dashboard-view__pill ${statusClass(health.status)}`}>{t(`meta.status.${health.status || 'unknown'}`)}</span>
              </div>
              <div className="dashboard-view__status-row">
                <span>{t('system.beat')}</span>
                <span className={`dashboard-view__pill ${runtime.beat?.detected ? 'dashboard-view__pill--green' : 'dashboard-view__pill--red'}`}>
                  {t(runtime.beat?.detected ? 'system.detected' : 'system.notDetected')}
                </span>
              </div>
            </div>
            <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <Link to="/console/meta" className="dashboard-view__table-link" style={{ fontSize: '12px' }}>
                Gestionar operaciones Meta <ArrowUpRight size={14} />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="dashboard-view__grid">
        <div className="dashboard-view__col-12">
          <motion.div className="dashboard-view__table-card" variants={itemVariants}>
            <div className="dashboard-view__table-header">
              <div className="dashboard-view__table-header-main">
                <h2 className="dashboard-view__table-title">{t('members.title')}</h2>
                <p className="dashboard-view__table-subtitle">{t('members.subtitle')}</p>
              </div>
              <Link to="/console/users" className="dashboard-view__table-link">
                {t('members.viewAll')} <ArrowUpRight size={16} />
              </Link>
            </div>
            <div className="dashboard-view__table-wrap">
              <table className="dashboard-view__table">
                <thead>
                  <tr>
                    <th>{t('members.user')}</th>
                    <th>{t('members.email')}</th>
                    <th>{t('members.type')}</th>
                    <th>{t('members.date')}</th>
                    <th className="is-right">{t('members.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.recent_users?.slice(0, 5).map(user => (
                    <tr key={user.id}>
                      <td>
                        <div className="dashboard-view__user-cell">
                          <div className="avatar-mini"><Users size={14} /></div>
                          <div>
                            <span>{user.username}</span>
                            {user.is_vip && <span className="dashboard-view__vip-mini">V.I.P.</span>}
                          </div>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        {user.is_superuser ? (
                          <span className="dashboard-view__type-badge dashboard-view__type-badge--gold">{t('members.superuser')}</span>
                        ) : user.user_type === 'business' || user.profile_type === 'business' ? (
                          <span className="dashboard-view__type-badge dashboard-view__type-badge--business">{t('members.business')}</span>
                        ) : (
                          <span className="dashboard-view__type-badge">{t('members.creator')}</span>
                        )}
                      </td>
                      <td>{new Intl.DateTimeFormat(locale).format(new Date(user.date_joined))}</td>
                      <td className="is-right">
                        <button
                          className="dashboard-view__action-btn"
                          onClick={() => navigate(`/console/users?search=${encodeURIComponent(user.username)}`)}
                          aria-label={t('members.openUser')}
                        >
                          <ArrowUpRight size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default AdminDashboard;
